import { useCallback, useEffect, useRef, useState } from "react";
import { connectManual, errorMessage, startConnect } from "../api";
import type { AuthMethod, CatalogProvider, ConnectionScope } from "../types";

export interface ConnectResult {
  ok: boolean;
  /** What to tell the user. Null when they simply closed the popup without deciding. */
  message: string | null;
}

/**
 * The mechanics of connecting an account: the OAuth popup and its postMessage reply, or
 * the paste-the-credential call. No UI and no opinion about steps -- the wizard owns
 * those, and it is the only thing that renders a connect flow now.
 *
 * `connect()` resolves only when the attempt is over, popup and all, so the caller can
 * simply await it and move on to its "done" step.
 */
export function useConnectFlow() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const popup = useRef<Window | null>(null);
  const poll = useRef<number | null>(null);
  /** The OAuth attempt currently waiting on a popup, if any. */
  const pending = useRef<{
    providerId: string;
    resolve: (result: ConnectResult) => void;
  } | null>(null);

  const settle = useCallback((result: ConnectResult) => {
    const inflight = pending.current;
    if (!inflight) return;
    pending.current = null;

    if (poll.current !== null) {
      clearInterval(poll.current);
      poll.current = null;
    }
    popup.current?.close();
    popup.current = null;

    setBusy(false);
    if (!result.ok && result.message) setError(result.message);
    inflight.resolve(result);
  }, []);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (event.data?.source !== "mochiconnector-oauth") return;

      // No attempt of ours is in flight: the popup belongs to someone else's flow.
      const inflight = pending.current;
      if (!inflight) return;
      if (event.data.providerId && event.data.providerId !== inflight.providerId) return;

      settle({ ok: event.data.ok === true, message: event.data.message ?? null });
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [settle]);

  // Closing the wizard mid-OAuth must not leave the poll running against a dead component.
  useEffect(
    () => () => {
      if (poll.current !== null) clearInterval(poll.current);
      pending.current = null;
    },
    []
  );

  const connect = useCallback(
    async (
      provider: CatalogProvider,
      method: AuthMethod,
      opts: {
        values?: Record<string, string>;
        connectionId?: string | null;
        /** Where a new connection lands. Ignored by the backend on reconnect. */
        projectId?: string;
        scope?: ConnectionScope;
      } = {}
    ): Promise<ConnectResult> => {
      setError(null);
      setBusy(true);
      const target = {
        ...(opts.connectionId ? { connectionId: opts.connectionId } : {}),
        ...(opts.projectId ? { projectId: opts.projectId } : {}),
        ...(opts.scope ? { scope: opts.scope } : {}),
      };

      if (method.kind === "oauth2") {
        try {
          const res = await startConnect({
            providerId: provider.id,
            methodId: method.id,
            ...target,
          });

          const w = 620;
          const h = 760;
          const left = window.screenX + (window.outerWidth - w) / 2;
          const top = window.screenY + (window.outerHeight - h) / 2;
          popup.current = window.open(
            res.data.url,
            `connect_${provider.id}`,
            `width=${w},height=${h},left=${left},top=${top}`
          );

          if (!popup.current) {
            const message = "Your browser blocked the popup. Allow popups and try again.";
            setBusy(false);
            setError(message);
            return { ok: false, message };
          }

          return await new Promise<ConnectResult>((resolve) => {
            pending.current = { providerId: provider.id, resolve };

            // The popup can close without ever reaching our callback (user hits X, or
            // bails on the consent screen). Without this poll the wizard waits forever.
            poll.current = window.setInterval(() => {
              if (popup.current?.closed) settle({ ok: false, message: null });
            }, 500);
          });
        } catch (err) {
          const message = errorMessage(err);
          setBusy(false);
          setError(message);
          return { ok: false, message };
        }
      }

      try {
        const res = await connectManual({
          providerId: provider.id,
          methodId: method.id,
          values: opts.values ?? {},
          ...target,
        });
        return {
          ok: true,
          message: `${provider.name} connected as ${res.data.accountLabel}.`,
        };
      } catch (err) {
        // The backend's validation message is written for a human ("that is not a Slack
        // webhook URL"), so it belongs against the form, not in a transient toast.
        const message = errorMessage(err);
        setError(message);
        return { ok: false, message };
      } finally {
        setBusy(false);
      }
    },
    [settle]
  );

  return { busy, error, setError, connect };
}
